import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HelpHint } from "@/components/HelpHint";
import { getStatusSemantic } from "@/lib/statusSemantics";
import { Sunrise, Navigation, AlarmClockCheck, Receipt } from "lucide-react";
import { navigationUrl } from "@/components/instructor-dashboard/LessonCard";

// Instructor Dashboard -> "Morning readiness" card. Extracted from
// InstructorDashboard.js (props-driven, no behavior change).
export const MorningReadinessCard = ({ data, onRunningLateForNext, onSubmitExpense }) => {
  const readinessSemantic = data.alerts_count > 0 ? getStatusSemantic("needs_attention") : getStatusSemantic("available");
  const nextPickupNav = data.todays_schedule?.[0] ? navigationUrl(data.todays_schedule[0]) : null;
  const ReadinessIcon = readinessSemantic.icon;

  return (
    <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 shadow-[0_1px_0_rgba(0,0,0,0.04)]" data-testid="instructor-morning-readiness-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
          <Sunrise className="h-5 w-5 text-[hsl(var(--role-accent))]" /> Morning readiness
        </h2>
        <div className="flex items-center gap-1">
          <Badge variant="outline" className={`gap-1.5 border ${readinessSemantic.badge}`} data-testid="instructor-readiness-status-badge">
            <ReadinessIcon className="h-3.5 w-3.5" />
            {data.alerts_count > 0 ? `${data.alerts_count} alert${data.alerts_count > 1 ? "s" : ""}` : "All good"}
          </Badge>
          <HelpHint text="A quick snapshot of whether you're ready to start your day." testId="morning-readiness-help" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <div className="rounded-lg border bg-secondary/40 p-3" data-testid="instructor-clock-in-status">
          <p className="text-xs text-muted-foreground">Clock-in status</p>
          <p className="text-sm font-medium mt-0.5">{data.clocked_in ? "Clocked in" : "Not clocked in"}</p>
        </div>
        <div className="rounded-lg border bg-secondary/40 p-3" data-testid="instructor-assigned-vehicle-summary">
          <p className="text-xs text-muted-foreground">Vehicle</p>
          <p className="text-sm font-medium mt-0.5 truncate">{data.assigned_vehicle ? `${data.assigned_vehicle.make} ${data.assigned_vehicle.model}` : "Not assigned"}</p>
        </div>
        <div className="rounded-lg border bg-secondary/40 p-3" data-testid="instructor-lessons-today-count">
          <p className="text-xs text-muted-foreground">Lessons today</p>
          <p className="text-sm font-medium mt-0.5">{data.lessons_today_count}</p>
        </div>
        <div className="rounded-lg border bg-secondary/40 p-3" data-testid="instructor-first-pickup-location">
          <p className="text-xs text-muted-foreground">First pickup</p>
          <p className="text-sm font-medium mt-0.5 truncate">{data.first_pickup_location || "No lessons today"}</p>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        {nextPickupNav ? (
          <a href={nextPickupNav} target="_blank" rel="noreferrer">
            <Button className="bg-[hsl(var(--role-accent))] hover:opacity-95 w-full sm:w-auto" data-testid="instructor-navigate-first-pickup-button">
              <Navigation className="h-4 w-4 mr-2" /> Navigate to first pickup
            </Button>
          </a>
        ) : (
          <Button className="bg-[hsl(var(--role-accent))] hover:opacity-95 w-full sm:w-auto" disabled data-testid="instructor-navigate-first-pickup-button">
            <Navigation className="h-4 w-4 mr-2" /> Navigate to first pickup
          </Button>
        )}
        <Button variant="outline" onClick={onRunningLateForNext} className="w-full sm:w-auto" data-testid="instructor-running-late-button">
          <AlarmClockCheck className="h-4 w-4 mr-2" /> Running late
        </Button>
        <Button variant="outline" onClick={onSubmitExpense} className="w-full sm:w-auto" data-testid="instructor-submit-expense-button">
          <Receipt className="h-4 w-4 mr-2" /> Submit expense
        </Button>
      </div>
    </div>
  );
};
